import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import {
	useReviewClarifications,
	useReviewSummary,
	useTriageClarification,
	useBatchTriage,
	useLinkDuplicates,

	useApplyToAll,
	useCompleteReviewStep,
	useAIReview,
} from "../../../hooks/useClarificationReview";
import { useHasAIAccess } from "hooks/featureFlags";
import { useInitialEvaluationContext } from "../../../hooks/InitialEvaluationContext";
import { ClarificationsRegister } from "newui/features/Rfx/Clarifications";
import type { Clarification, ClarificationStatus } from "newui/features/Rfx/Clarifications/types/clarification.type";

import type {
	ReviewClarification,
	TriageStatus,
	DeclineReason,
} from "../../../types/clarification-review.type";
import type { PanelMode } from "../../../types/panel.type";
import { ClarificationReviewTable } from "./components/ClarificationReviewTable";
import { ReviewProgressBar } from "./components/ReviewProgressBar";
import { BatchActionsBar } from "./components/BatchActionsBar";
import { AIReviewPanel } from "./components/AIReviewPanel";
import { DuplicateSelectionModal } from "./components/DuplicateSelectionModal";
import { ReviewClarificationModal } from "./components/ReviewClarificationModal";
import { ApplyToAllModal } from "./components/ApplyToAllModal";
import { StepCompleteModal } from "./components/StepCompleteModal";
import { FiPlus } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { EvaluationClarificationModal } from "../InitialEvaluationStep/panel/clarifications";
import { clarificationsRegisterSteps, buildClarificationsReviewSteps } from "guides/features/Evaluation";
import { showToast } from "utils/helpers";

interface ClarificationsStepProps {
	evalId?: number;
	isLoading?: boolean;
	isRfxLead?: boolean;
	panelMode?: PanelMode;
	rfxId: number;
	rfxSubmissionId?: number;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
}

const isPanelModeType = (mode?: PanelMode): boolean => {
	return mode === "manual_panel" || mode === "ai_panel";
};

export const ClarificationsStep: React.FC<ClarificationsStepProps> = ({
	evalId,
	rfxId,
	rfxSubmissionId,
	isLoading = false,
	isRfxLead = false,
	panelMode,
	onBeforeNext,
}) => {
	const { suppliers: evalSuppliers } = useInitialEvaluationContext();
	const allSuppliers = useMemo(() =>
		evalSuppliers.map((s) => ({ id: s.inviteId, name: s.supplierName, logo: s.supplierLogo })),
		[evalSuppliers]
	);
	const { data: clarifications, isLoading: isLoadingClarifications, error } = useReviewClarifications(rfxId);
	const summary = useReviewSummary(clarifications);
	const triageMutation = useTriageClarification(rfxId);
	const batchMutation = useBatchTriage(rfxId);
	const linkDuplicatesMutation = useLinkDuplicates(rfxId);

	const applyToAllMutation = useApplyToAll(rfxId);
	const completeStepMutation = useCompleteReviewStep(rfxId);
	const aiReviewMutation = useAIReview(rfxId);
	const hasAIAccess = useHasAIAccess();
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [showDuplicateModal, setShowDuplicateModal] = useState(false);
	const [duplicateCandidates, setDuplicateCandidates] = useState<ReviewClarification[]>([]);
	const [reviewingClarificationId, setReviewingClarificationId] = useState<number | null>(null);
	const [applyToAllClarification, setApplyToAllClarification] = useState<ReviewClarification | null>(null);
	const [showCompleteModal, setShowCompleteModal] = useState(false);
	const [showAddClarificationModal, setShowAddClarificationModal] = useState(false);
	const draftCountRef = useRef(0);
	const handleDraftCountChange = useCallback((count: number) => {
		draftCountRef.current = count;
	}, []);
	const [tabIndex, setTabIndex] = useState(0);
	const hasSetInitialTab = useRef(false);
	const completeResolveRef = useRef<((proceed: boolean) => void) | null>(null);

	const clarificationModalSuppliers = useMemo(() =>
		evalSuppliers.map((s) => ({ inviteId: s.inviteId, supplierLogo: s.supplierLogo, supplierName: s.supplierName })),
		[evalSuppliers]
	);

	const [guideDummyRows, setGuideDummyRows] = useState<Clarification[]>([]);

	const injectDummyRow = useCallback(() => {
		if (!evalSuppliers.length) return;
		const supplier = evalSuppliers[0];
		const now = new Date().toISOString();
		const base = {
			clarification_type_id: 1,
			conversation_unread_count: 0,
			created_at: now,
			files: [],
			is_complete: false,
			is_draft_response: false,
			is_approved: true,
			owner: "Guide",
			rfx_id: rfxId,
			rfx_submission_id: supplier.inviteId,
			supplier_name: supplier.supplierName,
			supplier_logo: supplier.supplierLogo,
			updated_at: now,
		};
		const draftRow: Clarification = {
			...base,
			id: -1,
			clarification_text: "Example draft clarification",
			reference_number: 1,
			is_sent: false,
			status: 1 as ClarificationStatus,
		};
		const sentRow: Clarification = {
			...base,
			id: -2,
			clarification_text: "Example sent clarification",
			reference_number: 2,
			is_sent: true,
			has_conversation: true,
			status: 2 as ClarificationStatus,
		};
		const completedRow: Clarification = {
			...base,
			id: -3,
			clarification_text: "Example completed clarification",
			reference_number: 3,
			is_sent: true,
			is_complete: true,
			has_conversation: true,
			status: 2 as ClarificationStatus,
		};
		setGuideDummyRows([draftRow, sentRow, completedRow]);
	}, [evalSuppliers, rfxId]);

	const removeDummyRow = useCallback(() => {
		setGuideDummyRows([]);
	}, []);

	const items = useMemo(() => clarifications ?? [], [clarifications]);
	const isPanel = isPanelModeType(panelMode);
	const reviewingClarification = reviewingClarificationId !== null
		? items.find((c) => c.id === reviewingClarificationId) ?? null
		: null;

	const clarificationInstructions = ["Review all proposed clarifications from panel members.", "Accept, decline, or mark as duplicate before sending to suppliers.", "Select multiple clarifications to triage in batch or mark as duplicates."];

	const soloClarificationInstructions = [
		"Create and manage clarification questions for suppliers.",
		"Suppliers will be notified of any new clarifications and can respond directly.",
		"Use the messaging functionality to communicate with the supplier against each clarification.",
		"All draft clarifications must be sent before clicking Next to proceed.",
	];

	const handleTriage = useCallback(
		(clarificationId: number, status: TriageStatus, declineReason?: string, declineCategory?: DeclineReason) => {
			triageMutation.mutate({
				clarificationId,
				payload: {
					triage_status: status,
					decline_reason: declineReason,
					decline_reason_category: declineCategory,
				},
			});
		},
		[triageMutation]
	);

	const handleMarkDuplicate = useCallback(
		(clarificationId: number) => {
			// Add this clarification to selected if not already
			const idsToUse = selectedIds.includes(clarificationId)
				? selectedIds
				: [...selectedIds, clarificationId];

			if (idsToUse.length < 2) {
				// Need at least 2 — select this one and prompt user to select more
				setSelectedIds(idsToUse);
				return;
			}

			const candidates = items.filter((c) => idsToUse.includes(c.id));
			setDuplicateCandidates(candidates);
			setShowDuplicateModal(true);
		},
		[selectedIds, items]
	);

	const handleLinkDuplicatesFromBatch = useCallback(() => {
		if (selectedIds.length < 2) {
			return;
		}
		const candidates = items.filter((c) => selectedIds.includes(c.id));
		setDuplicateCandidates(candidates);
		setShowDuplicateModal(true);
	}, [selectedIds, items]);

	const handleConfirmDuplicates = useCallback(
		(mainId: number, duplicateIds: number[]) => {
			linkDuplicatesMutation.mutate(
				{
					clarificationId: mainId,
					payload: {
						main_clarification_id: mainId,
						duplicate_ids: duplicateIds,
					},
				},
				{
					onSuccess: () => {
						setShowDuplicateModal(false);
						setDuplicateCandidates([]);
						setSelectedIds([]);
					},
				}
			);
		},
		[linkDuplicatesMutation]
	);

	const handleBatchAccept = useCallback(() => {
		if (selectedIds.length === 0) {
			return;
		}
		batchMutation.mutate(
			{
				clarification_ids: selectedIds,
				triage_status: "accepted",
			},
			{
				onSuccess: () => {
					setSelectedIds([]);
				},
			}
		);
	}, [selectedIds, batchMutation]);

	const handleRowClick = useCallback((clarification: ReviewClarification) => {
		setReviewingClarificationId(clarification.id);
	}, []);


	const uniqueSupplierCount = new Set(items.map((c) => c.rfx_invite_id)).size;

	const handleApplyToAll = useCallback(
		(clarificationId: number) => {
			const clarification = items.find((c) => c.id === clarificationId);
			if (clarification) {
				setApplyToAllClarification(clarification);
			}
		},
		[items]
	);

	const handleConfirmApplyToAll = useCallback(
		(clarificationId: number) => {
			applyToAllMutation.mutate(
				{
					clarificationId,
					payload: { clarification_id: clarificationId },
				},
				{
					onSuccess: () => {
						setApplyToAllClarification(null);
					},
				}
			);
		},
		[applyToAllMutation]
	);

	const handleBatchDecline = useCallback(
		(reason: string, category: DeclineReason) => {
			if (selectedIds.length === 0) {
				return;
			}
			batchMutation.mutate(
				{
					clarification_ids: selectedIds,
					triage_status: "declined",
					decline_reason: reason,
					decline_reason_category: category,
				},
				{
					onSuccess: () => {
						setSelectedIds([]);
					},
				}
			);
		},
		[selectedIds, batchMutation]
	);

	const handleCompleteConfirm = useCallback((dateDue?: string) => {
		completeStepMutation.mutate(
			{ date_due: dateDue },
			{
				onSuccess: () => {
					setShowCompleteModal(false);
					if (completeResolveRef.current) {
						completeResolveRef.current(true);
						completeResolveRef.current = null;
					}
				},
				onError: () => {
					// Keep modal open on error — user can retry or cancel
				},
			}
		);
	}, [completeStepMutation]);

	const handleCompleteCancel = useCallback(() => {
		setShowCompleteModal(false);
		if (completeResolveRef.current) {
			completeResolveRef.current(false);
			completeResolveRef.current = null;
		}
	}, []);

	const handleRequestAIReview = useCallback(() => {
		aiReviewMutation.mutate();
	}, [aiReviewMutation]);

	const handleAcceptAISuggestion = useCallback(
		(clarificationId: number, status: TriageStatus, declineReason?: string, declineCategory?: DeclineReason) => {
			handleTriage(clarificationId, status, declineReason, declineCategory);
		},
		[handleTriage]
	);

	const handleAcceptAIDuplicateGroup = useCallback(
		(mainId: number, duplicateIds: number[]) => {
			linkDuplicatesMutation.mutate({
				clarificationId: mainId,
				payload: {
					main_clarification_id: mainId,
					duplicate_ids: duplicateIds,
				},
			});
		},
		[linkDuplicatesMutation]
	);

	useEffect(() => {
		if (!hasSetInitialTab.current && !isLoadingClarifications && summary.total > 0) {
			hasSetInitialTab.current = true;
			if (summary.pending === 0) {
				setTabIndex(1);
			}
		}
	}, [isLoadingClarifications, summary.total, summary.pending]);

	useEffect(() => {
		if (onBeforeNext && isRfxLead) {
			if (isPanel) {
				onBeforeNext(() => {
					if (summary.pending > 0) {
						showToast(
							`${summary.pending} clarification${summary.pending !== 1 ? "s" : ""} still need to be reviewed before proceeding`,
							"Warning",
							false
						);
						return Promise.resolve(false);
					}
					return new Promise<boolean>((resolve) => {
						completeResolveRef.current = resolve;
						setShowCompleteModal(true);
					});
				});
			} else {
				onBeforeNext(async () => {
					if (draftCountRef.current > 0) {
						showToast(
							`${draftCountRef.current} draft clarification${draftCountRef.current !== 1 ? "s" : ""} must be sent or deleted before proceeding`,
							"Warning",
							false
						);
						return false;
					}
					return true;
				});
			}
		}
	}, [onBeforeNext, isRfxLead, isPanel, summary.pending, rfxId]);

	if (isLoading || isLoadingClarifications) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	const reviewContent = (
		<>
			<InstructionsInfoBox
				instructions={clarificationInstructions}
				guideSteps={buildClarificationsReviewSteps({
					hasClarifications: items.length > 0,
					canManage: isRfxLead,
				})}
			/>

			<ReviewProgressBar summary={summary} />

			{isRfxLead && hasAIAccess && (
				<AIReviewPanel
					rfxId={rfxId}
					clarifications={items}
					aiReview={aiReviewMutation.data ?? null}
					isLoading={aiReviewMutation.isPending}
					onRequestReview={handleRequestAIReview}
					onAcceptSuggestion={handleAcceptAISuggestion}
					onAcceptDuplicateGroup={handleAcceptAIDuplicateGroup}
				/>
			)}

			{isRfxLead && (
				<BatchActionsBar
					selectedCount={selectedIds.length}
					onBatchAccept={handleBatchAccept}
					onBatchDecline={handleBatchDecline}
					onLinkDuplicates={handleLinkDuplicatesFromBatch}
					onClearSelection={() => setSelectedIds([])}
					isProcessing={batchMutation.isPending}
				/>
			)}

			<ClarificationReviewTable
				clarifications={items}
				isLoading={isLoadingClarifications}
				error={error}
				selectedIds={selectedIds}
				onSelectionChange={setSelectedIds}
				onTriage={handleTriage}
				onMarkDuplicate={handleMarkDuplicate}
				onApplyToAll={isRfxLead ? handleApplyToAll : undefined}
				onRowClick={handleRowClick}
				isTriaging={triageMutation.isPending}
				isReadOnly={!isRfxLead}
			/>
		</>
	);

	const leadModals = isRfxLead ? (
		<>
			<ReviewClarificationModal
				isOpen={reviewingClarificationId !== null}
				clarification={reviewingClarification}
				onClose={() => setReviewingClarificationId(null)}
				onTriage={handleTriage}
				isTriaging={triageMutation.isPending}
			/>

			<ApplyToAllModal
				isOpen={applyToAllClarification !== null}
				clarification={applyToAllClarification}
				supplierCount={Math.max(0, uniqueSupplierCount - 1)}
				onClose={() => setApplyToAllClarification(null)}
				onConfirm={handleConfirmApplyToAll}
				isLoading={applyToAllMutation.isPending}
			/>

			<DuplicateSelectionModal
				isOpen={showDuplicateModal}
				clarifications={duplicateCandidates}
				onClose={() => {
					setShowDuplicateModal(false);
					setDuplicateCandidates([]);
				}}
				onConfirm={handleConfirmDuplicates}
				isLoading={linkDuplicatesMutation.isPending}
			/>

			<StepCompleteModal
				isOpen={showCompleteModal}
				summary={summary}
				onClose={handleCompleteCancel}
				onConfirm={handleCompleteConfirm}
				isLoading={completeStepMutation.isPending}
			/>
		</>
	) : null;

	// Panel mode + RFx Lead: tabbed layout with Review and Accepted Clarifications
	if (isRfxLead && isPanel) {
		return (
			<div data-testid="clarifications-step" className="space-y-4">
				<Tabs data-testid="clarifications-tabs" isLazy index={tabIndex} onChange={setTabIndex}>
					<TabList>
						<Tab data-testid="tab-review">
							Review{summary.pending > 0 && ` (${summary.pending})`}
						</Tab>
						<Tab data-testid="tab-accepted-clarifications">Clarifications Register</Tab>
					</TabList>
					<TabPanels>
						<TabPanel data-testid="tab-panel-review" className="space-y-4 px-0">
							{reviewContent}
						</TabPanel>
						<TabPanel data-testid="tab-panel-accepted-clarifications" className="space-y-4 px-0">
							<div className="flex justify-end">
								<Button
									onClick={() => setShowAddClarificationModal(true)}
									className="flex items-center gap-2"
									data-testid="add-clarification-button"
								>
									<FiPlus className="h-4 w-4" />
									Add Clarification
								</Button>
							</div>
							<ClarificationsRegister
								rfxId={rfxId}
								allSuppliers={allSuppliers}
								isClientView={true}
								supplierName=""
							/>
						</TabPanel>
					</TabPanels>
				</Tabs>
				{leadModals}
				{showAddClarificationModal && evalId && (
					<EvaluationClarificationModal
						rfxId={rfxId}
						evalId={evalId}
						isOpen={showAddClarificationModal}
						suppliers={clarificationModalSuppliers}
						showSubmitAndAnother
						onClose={() => setShowAddClarificationModal(false)}
					/>
				)}
			</div>
		);
	}

	// Solo mode + RFx Lead: show clarifications register directly (no triage)
	if (isRfxLead && !isPanel) {
		return (
			<div data-testid="clarifications-step" className="space-y-4">
				<InstructionsInfoBox
					instructions={soloClarificationInstructions}
					guideSteps={clarificationsRegisterSteps}
					onBeforeShowMe={injectDummyRow}
					onAfterShowMe={removeDummyRow}
				/>
				<div className="flex justify-end">
					<Button
						onClick={() => setShowAddClarificationModal(true)}
						className="flex items-center gap-2"
						data-testid="add-clarification-button"
					>
						<FiPlus className="h-4 w-4" />
						Add Clarification
					</Button>
				</div>
				<ClarificationsRegister
					rfxId={rfxId}
					allSuppliers={allSuppliers}
					injectedClarifications={guideDummyRows}
					isClientView={true}
					supplierName=""
					onDraftCountChange={handleDraftCountChange}
				/>
				{showAddClarificationModal && evalId && (
					<EvaluationClarificationModal
						rfxId={rfxId}
						evalId={evalId}
						isOpen={showAddClarificationModal}
						suppliers={clarificationModalSuppliers}
						showSubmitAndAnother
						onClose={() => setShowAddClarificationModal(false)}
					/>
				)}
			</div>
		);
	}

	// Non-lead users: read-only ClarificationsRegister
	return (
		<div data-testid="clarifications-step" className="space-y-4">
			<ClarificationsRegister
				rfxId={rfxId}
				isClientView={false}
				supplierName=""
			/>
		</div>
	);
};
